import type { OMSOrderPropertyEnum } from '@talos/kyoko';
import type { PreTradeAnalytic } from '../../../redux/preTradeApiTypes';
import { parseFiniteNumber } from '../../../utils';
import {
  NUMERIC_FIELDS,
  type PublicMarketRowData,
  type PublicMarketsFieldName,
  type PublicMarketsNumericFields,
  propertyToField,
} from './types';

function getRowFieldName(property: OMSOrderPropertyEnum): PublicMarketsFieldName | undefined {
  return propertyToField[property];
}

/**
 * Converts pre-trade analytics into public market row data.
 * Groups analytics by the composite key of Market + Symbol to avoid collisions
 * (the same symbol can appear on multiple markets with unified liquidity).
 * Falls back to Market alone when Symbol is absent. Returns rows sorted by id.
 *
 * @param analytics - Raw pre-trade analytics from the API
 * @returns Public market rows, one per unique market/symbol combination
 */
export function parsePublicMarkets(analytics: PreTradeAnalytic[]): PublicMarketRowData[] {
  const rows = new Map<string, PublicMarketRowData>();

  for (const analytic of analytics) {
    if (!analytic.Market) {
      continue;
    }

    const numericValue = parseFiniteNumber(analytic.Value);
    if (numericValue == null) {
      continue;
    }

    const field = getRowFieldName(analytic.Property);
    if (!field) {
      continue;
    }

    const key = analytic.Symbol ? `${analytic.Market}:${analytic.Symbol}` : analytic.Market;

    if (!rows.has(key)) {
      rows.set(key, {
        id: key,
        marketName: analytic.Market,
        symbol: analytic.Symbol,
        volumeHorizon: null,
        adv: null,
        advPct: null,
        spread: null,
        spreadDaily: null,
        volatility: null,
        volatilityDaily: null,
      });
    }

    const row = rows.get(key)!;
    row[field] = numericValue;
  }

  return [...rows.values()].toSorted((left, right) => left.id.localeCompare(right.id));
}

function calculateMaxValue(values: (number | null)[]): number | null {
  const validValues = values.filter((v): v is number => v !== null);
  if (validValues.length === 0) {
    return null;
  }
  const max = Math.max(...validValues);
  return max > 0 ? max : null;
}

/**
 * Calculate max values for each column from the row data.
 * Used to scale the bar widths so the largest value gets 100% width.
 * Returns null for columns with no valid values (all nulls or all zeros).
 * Cells with null values show a dash and no bar, so maxValue is never used for
 * bar width in those cases.
 */
export function calculatePublicMarketsMaxValues(rows: PublicMarketRowData[]) {
  const result: PublicMarketsNumericFields = {
    volumeHorizon: null,
    adv: null,
    advPct: null,
    spread: null,
    spreadDaily: null,
    volatility: null,
    volatilityDaily: null,
  };
  for (const numericFieldName of NUMERIC_FIELDS) {
    result[numericFieldName] = calculateMaxValue(rows.map(r => r[numericFieldName]));
  }
  return result;
}
